import { CellPosition, Column, EditableCallbackParams, IRowNode } from 'ag-grid-community';
import { findLastIndex } from '../../utils/arrayUtils';
type Position = {
    idx: number;
    rowIdx: number;
};

/**
 * Gets the next column position. If the current column is at the end of the row,
 * it jumps to the start of the next row, if there is a next row.
 *
 * @param columns
 * @param currentCell
 * @param rowCount
 */
// export const getNextColumnPosition = (columns: number[], currentCell: CellPosition, rowCount: number) => {
//     if (columns.length === 0) {
//         return currentCell;
//     }

//     const lowestColIdx = Math.min(...columns);
//     const highestColIdx = Math.max(...columns);
//     const currColIdx = currentCell.;

//     if (currColIdx >= highestColIdx) {
//         // At the bottom row, can't go forward anymore
//         if (currentCell.rowIdx >= (rowCount - 1)){
//             return currentCell;
//         }

//         return {
//             idx: lowestColIdx,
//             rowIdx: currentCell.rowIdx + 1
//         }
//     }

//     return {
//         idx: columns.find(x => x > currColIdx) ?? columns[columns.length - 1],
//         rowIdx: currentCell.rowIdx
//     }
// };

const isColumnEditable = (column: Column, rowNode: IRowNode | undefined) => {
    const colDefEditable = column?.getColDef().editable ?? false;
    const editable =
        typeof colDefEditable === 'function'
            ? colDefEditable({ node: rowNode, data: rowNode?.data } as EditableCallbackParams)
            : (colDefEditable as boolean);
    return editable;
};

export const getNextColumnPosition = <T>(
    rowCount: number,
    columns: Column[],
    backwards: boolean,
    currentCell: CellPosition,
    rowNode: IRowNode<T> | undefined
) => {
    let nextCell = null as CellPosition | null;
    const currentColField = currentCell.column.getColId();

    let traverse = false;
    const index = columns.findIndex(c => c.getId() == currentColField);
    const lowestEditableColIdx = columns.findIndex(c => isColumnEditable(c, rowNode));
    const colCopy = [...columns].reverse();
    const highestEditableColIdx = colCopy.length - (colCopy.findIndex(c => isColumnEditable(c, rowNode)) + 1);

    const nextEditableColIdx = columns.findIndex((c, idx) => idx > index && isColumnEditable(c, rowNode));
    const previousEditableColIdx = findLastIndex(columns, (c, idx) => {
        if (rowNode) {
            const editable = isColumnEditable(c, rowNode);

            return idx < index && editable;
        }
        return false;
    });
    let nextRowIndex = currentCell.rowIndex;

    if (!backwards) {
        if (index < highestEditableColIdx) {
            nextCell = { column: columns[nextEditableColIdx] } as CellPosition;
        } else {
            nextCell = { column: columns[lowestEditableColIdx] } as CellPosition;
            traverse = true;
        }
    } else {
        if (index - 1 >= lowestEditableColIdx) {
            nextCell = { column: columns[previousEditableColIdx] } as CellPosition;
        } else {
            if (nextRowIndex > 0) {
                nextCell = { column: columns[highestEditableColIdx] } as CellPosition;
                traverse = true;
            } else {
                nextCell = { column: columns[columns.length - 1] } as CellPosition;
                traverse = true;
            }
        }
    }

    if (traverse) {
        nextRowIndex = backwards ? currentCell.rowIndex - 1 : currentCell.rowIndex + 1;
    }

    if (nextRowIndex < 0) {
        nextRowIndex = -1;
    }
    if (nextRowIndex >= rowCount) {
        nextRowIndex = rowCount - 1;
    }
    return { ...nextCell, rowIndex: nextRowIndex } as CellPosition;
};

/**
 * Gets the previous column position. If the current column is at the beginning of the row,
 * it jumps to the end of the previous row, if there is a previous row.
 *
 * @param columns
 * @param currentCell
 * @param rowCount
 */
export const getPreviousColumnPosition = (columns: number[], currentCell: Position) => {
    columns = [...columns].reverse();
    if (columns.length === 0) {
        return currentCell;
    }

    const lowestColIdx = Math.min(...columns);
    const highestColIdx = Math.max(...columns);
    const currColIdx = currentCell.idx;

    if (currColIdx <= lowestColIdx) {
        // At the top row, can't go back anymore
        if (currentCell.rowIdx <= 0) {
            return currentCell;
        }

        return {
            idx: highestColIdx,
            rowIdx: currentCell.rowIdx - 1
        };
    }

    return {
        idx: columns.find(x => x < currColIdx) ?? columns[0],
        rowIdx: currentCell.rowIdx
    };
};
